import { Injectable } from '@angular/core';
import { Observable, of as observableOf } from 'rxjs';
import { filter, map, share, switchMap, tap } from 'rxjs/operators';

import { BufferQueue } from 'core/buffer-queue';

import {
  GetTagByIdForQueryGQL, GetTagsByIdsForQuery, GetTagsByIdsForQueryGQL, TagGroupInfoFragment, TagInfoFragmentDoc
} from 'generated/graphql';
import { Apollo } from 'apollo-angular';

/**
 * Used by Query builder to
 * resolve the tag name and categories
 * from IDs on demand and in batches.
 */
@Injectable({
  providedIn: 'root'
})
export class BatchTagResolverService {

  /** Internal cache to the service */
  private cache = new Map<string, GetTagsByIdsForQuery.GetTagsByIds>();

  /** A buffer queue  */
  private queue: BufferQueue<string>;

  /** Observable of the queue results.
   * You normally will want to subscribe to the result
   * from getTag(tagId: string)
   */
  public resolvedQueue: Observable<any>;

  /**
   *
   */
  constructor(
    private getTagByIdForQueryGQL: GetTagByIdForQueryGQL,
    private getTagsByIdsForQueryGQL: GetTagsByIdsForQueryGQL,
    private apollo: Apollo,
  ) {
    this.queue = new BufferQueue<string>();
    this.resolvedQueue = this.queue.bufferedQueue$.pipe(
      share(), // Important to use share or each subscriber ends up with a new buffer.
      switchMap((tagIds) => {
        if (tagIds.length === 1) {
          // When we only have one tag in the queue, this will attempt to use the cache
          return this.getTagByIdForQueryGQL.fetch({ tagId: tagIds[0] })
            .pipe(
              map(({ data }) => data.getTagById),
              map((tag) => [tag])
            );
        }
        return this.getTagsByIdsForQueryGQL.fetch({ tagIds })
          .pipe(
            map(({ data }) => data.getTagsByIds)
          );
      })
    );
  }

  getTag(tagId: string) {
    this._syncLocalCacheWithApollo([tagId]);

    if (this.cache.has(tagId)) {
      return observableOf(this.cache.get(tagId));
    }

    this.queue.addItem(tagId);

    return this.resolvedQueue.pipe(
      filter(tags => tags.some(t => t.id === tagId)),
      map<GetTagsByIdsForQuery.GetTagsByIds[], GetTagsByIdsForQuery.GetTagsByIds>(tags => tags.find(t => t.id === tagId)),
      tap(tag => {
        this.cache.set(tagId, tag);
      })
    );
  }

  getTags(tagIds: string[]) {
    this._syncLocalCacheWithApollo(tagIds);

    const uncachedTags = tagIds.filter(t => !this.cache.has(t));
    if (uncachedTags.length === 0) {
      return observableOf(tagIds.map(tId => this.cache.get(tId)));
    }

    return this.getTagsByIdsForQueryGQL
      .fetch({ tagIds: uncachedTags })
      .pipe(
        tap(res => {
          res.data
            .getTagsByIds.forEach(tag => this.cache.set(tag.id, tag));
        }),
        map(_ => tagIds.map(tId => this.cache.get(tId)))
      );
  }

  private readFromApolloCache(tagId: string): TagGroupInfoFragment {
    const tagRes = this.apollo.client.readFragment<TagGroupInfoFragment>({
      fragment: TagInfoFragmentDoc,
      fragmentName: 'TagInfo',
      id: `Tag:${tagId}`,
    });

    return tagRes;
  }

  private _syncLocalCacheWithApollo(tagIds: string[]) {
    const uncachedTags = tagIds.filter(t => !this.cache.has(t));

    uncachedTags
      .map(tId => this.readFromApolloCache(tId))
      .filter(tag => tag !== null)
      .forEach((tag) => this.cache.set(tag.id, tag as any));
  }

  clearCache() {
    this.cache = new Map<string, GetTagsByIdsForQuery.GetTagsByIds>();
  }
}
